'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { useCart } from '@sola/cart-sdk/store.tsx';
import { suppressForLiveEvent } from '@sola/checkout-sdk/utils/campaigns.ts';
import { CartItemTypeEnum } from '@sola/schema/dotcom/cart/cart-item.ts';
import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import { useContentSettingsValue } from '@sola/sdg-sdk/hooks.ts';
import { isItemGiftBirthday, isItemSponsorshipFirstPayment, isItemSponsorshipSelected } from '@sola/shared-utils/cart/cart-item-type-guards.ts';
import { birthdayAppealAmounts, birthdayAppealDefaultAmount, birthdayAppealFrequency } from './birthday-appeal.config.ts';
import type { BirthdayAppealAmountOption } from './birthday-appeal.logic.ts';
export interface UseBirthdayAppealCartItemResult {
  childName: string;
  amountOptions: BirthdayAppealAmountOption[];
  defaultAmount: string;
  isEligible: boolean;
  isAdded: boolean;
  isBusy: boolean;
  onAdd: (amount: string) => Promise<void>;
  onRemove: () => Promise<void>;
}
export const useBirthdayAppealCartItem = item => {
  const $ = _c(32);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    cart,
    itemsBusy,
    actions
  } = useCart("cart", "itemsBusy", "actions");
  const {
    data: giftAmountsSetting
  } = useContentSettingsValue("giftAmounts");
  const globalId = item.child?.globalId ?? "";
  const childName = item.child?.name ?? "";
  let t1;
  if ($[1] !== cart || $[2] !== globalId || $[3] !== item) {
    t1 = !suppressForLiveEvent(cart) && (isItemSponsorshipSelected(item) || isItemSponsorshipFirstPayment(item)) && !!globalId;
    $[1] = cart;
    $[2] = globalId;
    $[3] = item;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const isEligible = t1;
  let defaultAmountValue;
  let existingGift;
  let t2;
  if ($[5] !== cart?.items || $[6] !== giftAmountsSetting?.birthday || $[7] !== globalId) {
    existingGift = cart?.items?.find(cartItem => isItemGiftBirthday(cartItem) && cartItem.gift.globalId === globalId);
    const settingsBirthday = giftAmountsSetting?.birthday;
    const settingsOptions = settingsBirthday?.options;
    const amountValues = settingsOptions && settingsOptions.length > 0 ? settingsOptions : [...birthdayAppealAmounts];
    defaultAmountValue = existingGift?.unitPrice ?? settingsBirthday?.default ?? birthdayAppealDefaultAmount;
    t2 = amountValues.includes(defaultAmountValue) ? amountValues : [...amountValues, defaultAmountValue].toSorted(_temp);
    $[5] = cart?.items;
    $[6] = giftAmountsSetting?.birthday;
    $[7] = globalId;
    $[8] = defaultAmountValue;
    $[9] = existingGift;
    $[10] = t2;
  } else {
    defaultAmountValue = $[8];
    existingGift = $[9];
    t2 = $[10];
  }
  const optionValues = t2;
  let t3;
  if ($[11] !== optionValues || $[12] !== t) {
    let t4;
    if ($[14] !== t) {
      t4 = amount => ({
        value: String(amount),
        label: t("core:numbers.currencyCustom", {
          context: birthdayAppealFrequency,
          amount
        })
      });
      $[14] = t;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    t3 = optionValues.map(t4);
    $[11] = optionValues;
    $[12] = t;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const amountOptions = t3;
  const defaultAmount = String(defaultAmountValue);
  let t4;
  if ($[16] !== actions || $[17] !== globalId || $[18] !== item.id) {
    t4 = async amount_0 => {
      await actions.addItems({
        items: {
          itemType: CartItemTypeEnum.Gift,
          commitmentFrequency: birthdayAppealFrequency,
          unitPrice: Number(amount_0),
          gift: {
            giftType: GiftTypeEnum.Birthday,
            globalId
          }
        },
        options: {
          parentId: item.id
        }
      });
    };
    $[16] = actions;
    $[17] = globalId;
    $[18] = item.id;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const onAdd = t4;
  let t5;
  if ($[20] !== actions || $[21] !== existingGift) {
    t5 = async () => {
      if (existingGift) {
        await actions.removeItem(existingGift.id);
      }
    };
    $[20] = actions;
    $[21] = existingGift;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const onRemove = t5;
  const t6 = !!existingGift;
  const t7 = !!itemsBusy?.length;
  let t8;
  if ($[23] !== amountOptions || $[24] !== childName || $[25] !== defaultAmount || $[26] !== isEligible || $[27] !== onAdd || $[28] !== onRemove || $[29] !== t6 || $[30] !== t7) {
    t8 = {
      childName,
      amountOptions,
      defaultAmount,
      isEligible,
      isAdded: t6,
      isBusy: t7,
      onAdd,
      onRemove
    };
    $[23] = amountOptions;
    $[24] = childName;
    $[25] = defaultAmount;
    $[26] = isEligible;
    $[27] = onAdd;
    $[28] = onRemove;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
};
function _temp(a, b) {
  return a - b;
}